import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function EinkaufsguidePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.quest';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-pie-chart-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-foreground-900 text-lg font-semibold whitespace-nowrap">OOPBUY Spreadsheet</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-foreground-600 text-sm font-medium hover:text-foreground-900 transition-colors whitespace-nowrap">Startseite</Link>
            <Link to="/artikel/oopbuy-spreadsheet-einkaufsguide" className="text-primary-500 text-sm font-semibold hover:text-primary-600 transition-colors whitespace-nowrap">Einkaufsguide</Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">Buy it now</a>
          </div>
        </div>
      </nav>

      <article className="pt-24 pb-16">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors"><i className="ri-home-4-line"></i></Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/" className="hover:text-primary-500 transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">OOPBUY Spreadsheet Einkaufsguide</span>
          </nav>

          <header className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-5">
              <i className="ri-shopping-cart-2-line text-xs"></i>
              <span>Schritt-für-Schritt</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 font-bold leading-tight">
              OOPBUY Spreadsheet Einkaufsguide: So kaufen Sie richtig ein
            </h1>
            <p className="mt-4 text-foreground-500 text-base md:text-lg leading-relaxed">
              Der komplette Einkaufsguide für das OOPBUY Spreadsheet. Von der Produktsuche über die Bestellung bis zum Versand – Schritt für Schritt erklärt für Einsteiger und Fortgeschrittene.
            </p>
            <div className="flex items-center gap-4 mt-6 text-foreground-400 text-sm">
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>Aktualisiert: Juli 2026</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>11 Min. Lesezeit</span>
            </div>
          </header>

          <div className="relative rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Step%20by%20step%20shopping%20guide%20concept%2C%20a%20persons%20hands%20holding%20a%20smartphone%20displaying%20a%20clean%20shopping%20interface%20with%20product%20listings%2C%20beside%20it%20a%20notebook%20with%20handwritten%20notes%20and%20checkmarks%2C%20a%20white%20ceramic%20coffee%20cup%2C%20a%20pair%20of%20white%20sneakers%20nearby%2C%20warm%20natural%20daylight%2C%20cozy%20modern%20workspace%2C%20soft%20sage%20green%20and%20cream%20color%20accents%2C%20editorial%20lifestyle%20photography%2C%20organized%20and%20intentional%20composition&width=1200&height=600&seq=einkaufsguide-hero-04&orientation=landscape"
              alt="OOPBUY Spreadsheet Einkaufsguide"
              className="w-full aspect-video object-cover"
            />
          </div>

          <div className="text-foreground-700 leading-relaxed space-y-10">
            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Schritt 1: Das OOPBUY Spreadsheet verstehen
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Bevor Sie mit dem Einkauf beginnen, sollten Sie das <strong>OOPBUY Spreadsheet</strong> gründlich kennenlernen. Das Spreadsheet ist in 14 Kategorien unterteilt, jede mit einer eigenen Liste geprüfter Produkte. Öffnen Sie das Spreadsheet auf <strong>kaspreadsheet.com</strong> und verschaffen Sie sich einen Überblick über die verfügbaren Kategorien – von Schuhen über Kleidung bis zu Accessoires.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Schritt 2: Die richtige Kategorie finden
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Das <strong>OOPBUY Spreadsheet</strong> macht die Navigation einfach. Jede der 14 Kategorien ist klar beschriftet und enthält eine kurze Beschreibung. Suchen Sie gezielt nach dem, was Sie brauchen:
              </p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { cat: 'Schuhe', icon: 'ri-footprint-line', desc: 'Sneaker & Sportschuhe' },
                  { cat: 'Hoodies', icon: 'ri-t-shirt-line', desc: 'Pullover & Sweater' },
                  { cat: 'T-Shirts', icon: 'ri-shirt-line', desc: 'Basic & Statement' },
                  { cat: 'Jacken', icon: 'ri-windy-line', desc: 'Outerwear' },
                  { cat: 'Hosen', icon: 'ri-checkbox-blank-line', desc: 'Jeans & Shorts' },
                  { cat: 'Accessoires', icon: 'ri-vip-diamond-line', desc: 'Schmuck & Uhren' },
                ].map((item) => (
                  <div key={item.cat} className="flex items-center gap-2.5 p-3 rounded-xl bg-background-100">
                    <i className={`${item.icon} text-primary-500 text-sm`}></i>
                    <div><div className="text-foreground-800 text-xs font-semibold">{item.cat}</div><div className="text-foreground-400 text-[10px]">{item.desc}</div></div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Schritt 3: Produkte vergleichen und QC prüfen
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Sobald Sie eine Kategorie ausgewählt haben, zeigt Ihnen das <strong>OOPBUY Spreadsheet</strong> alle verfügbaren Produkte mit ihren QC-Bewertungen an. Achten Sie besonders auf:
              </p>
              <ul className="mt-4 space-y-2.5">
                {[
                  'Die Sterne-Bewertung (1–5) für die Gesamtqualität',
                  'Detaillierte QC-Fotos von Material, Nähten und Details',
                  'Größenempfehlungen und Passform-Hinweise',
                  'Preis-Leistungs-Verhältnis im Vergleich zu ähnlichen Produkten',
                  'Aktualität des Eintrags – neuere Einträge haben oft aktuellere Informationen',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 text-background-50 text-xs font-bold">{i + 1}</span>
                    <span className="text-foreground-600 text-sm leading-relaxed pt-0.5">{tip}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Schritt 4: Den Kauf über kaspreadsheet.com abwickeln
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Sobald Sie sich für ein Produkt entschieden haben, klicken Sie auf den entsprechenden Link im <strong>OOPBUY Spreadsheet</strong>. Sie werden direkt zur Produktseite auf <strong>kaspreadsheet.com</strong> weitergeleitet, wo Sie den Kauf abschließen können. Alle wichtigen Informationen – Preis, Größe, Farbe – werden direkt aus dem Spreadsheet übernommen.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Schritt 5: Versand auswählen und bestellen
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Nach dem Kauf wird Ihr Produkt zum Lager des Shopping-Agenten gesendet. Dort erhalten Sie QC-Fotos und können das Produkt vor dem Weiterversand prüfen. Das <strong>OOPBUY Spreadsheet</strong> enthält Empfehlungen für die besten Versandoptionen:
              </p>
              <div className="mt-5 space-y-3">
                {[
                  { title: 'Triangle Shipping', desc: 'Empfohlen für Bestellungen nach Deutschland. Vermeidet Zollprobleme, da das Paket über ein EU-Land eingeführt wird.' },
                  { title: 'Direct Shipping', desc: 'Schneller, aber mit Zollrisiko. Nur bei kleinen Bestellungen unter €150 empfehlenswert.' },
                  { title: 'Consolidation', desc: 'Mehrere Produkte in einem Paket bündeln. Spart Versandkosten, erhöht aber das Zollrisiko bei hohem Warenwert.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-box-3-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-900 font-semibold text-sm mb-1">{item.title}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Tipps für den erfolgreichen Einkauf
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: 'Budget planen', desc: 'Kalkulieren Sie Produktpreis + Versand + eventuelle Zollgebühren. Als Faustregel: 30–50 % des Produktpreises für Versand einplanen.' },
                  { title: 'QC-Fotos anfordern', desc: 'Lassen Sie sich immer QC-Fotos schicken, bevor das Paket versendet wird. So können Sie Probleme frühzeitig erkennen.' },
                  { title: 'Größentabellen nutzen', desc: 'Asiatische Größen fallen oft kleiner aus. Nutzen Sie die Größentabellen im OOPBUY Spreadsheet und messen Sie zur Sicherheit nach.' },
                  { title: 'Bewertungen lesen', desc: 'Lesen Sie die Community-Bewertungen im Spreadsheet. Sie enthalten oft wertvolle Hinweise, die in den reinen QC-Daten nicht sichtbar sind.' },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                      <i className="ri-lightbulb-line text-accent-600 text-base"></i>
                    </div>
                    <h3 className="text-foreground-900 font-semibold text-sm mb-1.5">{item.title}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/50">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 font-bold mb-3">
                Ihr erster Einkauf mit dem OOPBUY Spreadsheet
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Mit diesem Einkaufsguide sind Sie bestens vorbereitet. Das <strong>OOPBUY Spreadsheet</strong> nimmt Ihnen die Unsicherheit beim China-Einkauf – von der Produktauswahl über die Qualitätskontrolle bis zum Versand. Starten Sie noch heute mit Ihrer ersten Bestellung und erleben Sie, wie einfach und sicher der Einkauf mit dem richtigen Werkzeug sein kann.
              </p>
            </section>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-background-100 border border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div>
                <h3 className="text-foreground-900 font-semibold text-lg">Bereit zum Einkaufen?</h3>
                <p className="text-foreground-500 text-sm mt-1">Nutzen Sie das OOPBUY Spreadsheet auf kaspreadsheet.com</p>
              </div>
              <div className="flex items-center gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">OOPBUY Spreadsheet</a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap">Buy it now</a>
              </div>
            </div>
          </div>

          <nav className="mt-10 pt-8 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors text-sm font-medium">
              <i className="ri-arrow-left-line"></i>Zurück zur Startseite
            </Link>
            <Link to="/artikel/oopbuy-spreadsheet-guide" className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors text-sm font-medium">
              Hauptguide lesen<i className="ri-arrow-right-line"></i>
            </Link>
          </nav>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'OOPBUY Spreadsheet Einkaufsguide: So kaufen Sie richtig ein',
        description: 'Der komplette Einkaufsguide f&uuml;r das OOPBUY Spreadsheet. Von der Produktsuche &uuml;ber die Bestellung bis zum Versand &ndash; Schritt f&uuml;r Schritt erkl&auml;rt f&uuml;r Einsteiger und Fortgeschrittene.',
        author: { '@type': 'Organization', name: 'OOPBUY Spreadsheet Guide' },
        publisher: { '@type': 'Organization', name: 'OOPBUY Spreadsheet Guide', logo: { '@type': 'ImageObject', url: siteUrl + '/favicon.ico' } },
        datePublished: '2026-07-01',
        dateModified: '2026-07-15',
        mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + '/artikel/oopbuy-spreadsheet-einkaufsguide' },
        inLanguage: 'de',
      }) }} />

      <footer className="relative bg-foreground-900 mx-4 md:mx-6 mb-4 md:mb-6 rounded-3xl overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-pie-chart-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading text-background-50 text-base font-semibold">OOPBUY Spreadsheet</span>
            </Link>
            <p className="text-foreground-500 text-xs">Unabhängige Informationsressource – nicht offiziell mit OOPBUY verbunden.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}